import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, fmtDateTime, todayISO } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import {
  Loader2, Plus, CalendarClock, CalendarCheck, Send, Clock, CheckCircle2, XCircle,
  ChevronLeft, ChevronRight, Users2, ThumbsUp, CalendarDays, MessageSquare,
} from "lucide-react";

const M_STATUS = {
  pendiente: { label: "Pendiente", badge: "bg-yellow-100 text-yellow-800 border-yellow-200", dot: "bg-yellow-500", border: "border-l-yellow-500" },
  aprobada: { label: "Confirmada", badge: "bg-emerald-100 text-emerald-800 border-emerald-200", dot: "bg-emerald-500", border: "border-l-emerald-500" },
  rechazada: { label: "Rechazada", badge: "bg-red-100 text-red-800 border-red-200", dot: "bg-red-500", border: "border-l-red-500" },
};

const WEEK_LABELS = ["L", "M", "X", "J", "V", "S", "D"];

function StatCard({ icon: Icon, label, value, color, bg, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={testid}>
      <div className={cn("mb-2 inline-flex h-9 w-9 items-center justify-center rounded-lg", bg)}>
        <Icon className={cn("h-5 w-5", color)} />
      </div>
      <div className="font-heading text-3xl font-extrabold text-slate-900">{value}</div>
      <div className="text-xs font-medium text-slate-500">{label}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const m = M_STATUS[status] || M_STATUS.pendiente;
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold", m.badge)}>
      <span className={cn("h-1.5 w-1.5 rounded-full", m.dot)} />{m.label}
    </span>
  );
}

const emptyForm = () => ({ title: "", topics: "", date: todayISO(), time: "09:00", end_time: "10:00", participant_ids: [] });

function RequestDialog({ open, onOpenChange }) {
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: dir = [] } = useQuery({ queryKey: ["directory"], queryFn: () => api.get("/directory").then((r) => r.data), enabled: open });
  const others = dir.filter((u) => u.id !== user?.id);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (id) => setForm((f) => ({ ...f, participant_ids: f.participant_ids.includes(id) ? f.participant_ids.filter((x) => x !== id) : [...f.participant_ids, id] }));

  const submit = async () => {
    if (!form.title.trim()) return toast.error("El título es obligatorio");
    if (form.participant_ids.length === 0) return toast.error("Selecciona al menos un participante");
    setSaving(true);
    try {
      await api.post("/meetings", form);
      toast.success("Solicitud de reunión enviada");
      qc.invalidateQueries();
      setForm(emptyForm());
      onOpenChange(false);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const involvesAdmin = others.some((u) => form.participant_ids.includes(u.id) && u.role === "admin");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="request-meeting-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Solicitar reunión</DialogTitle>
          <DialogDescription>Elige día, hora, participantes y los temas a tratar.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div><Label>Título *</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Ej: Revisión de metas del mes" className="mt-1" data-testid="meeting-title-input" /></div>
          <div><Label>Temas a tratar</Label><Textarea value={form.topics} onChange={(e) => set("topics", e.target.value)} placeholder="Puntos de la agenda" rows={3} className="mt-1" data-testid="meeting-topics-input" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Fecha</Label><Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className="mt-1" data-testid="meeting-date-input" /></div>
            <div><Label>Inicio</Label><Input type="time" value={form.time} onChange={(e) => set("time", e.target.value)} className="mt-1" data-testid="meeting-time-input" /></div>
            <div><Label>Fin</Label><Input type="time" value={form.end_time} onChange={(e) => set("end_time", e.target.value)} className="mt-1" data-testid="meeting-end-time-input" /></div>
          </div>
          <div>
            <Label>Participantes *</Label>
            <div className="mt-2 max-h-52 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2">
              {others.map((u) => (
                <label key={u.id} className="flex cursor-pointer items-center gap-3 rounded-lg p-2 hover:bg-slate-50" data-testid={`participant-${u.id}`}>
                  <Checkbox checked={form.participant_ids.includes(u.id)} onCheckedChange={() => toggle(u.id)} />
                  <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-slate-200 text-xs font-bold text-slate-600">
                    {u.avatar ? <img src={u.avatar} alt="" className="h-full w-full object-cover" /> : u.name[0]}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-slate-800">{u.name}</div>
                    <div className="text-[11px] text-slate-400">{u.role === "admin" ? "Administrador" : "Funcionario"}</div>
                  </div>
                </label>
              ))}
              {others.length === 0 && <p className="p-2 text-sm text-slate-400">No hay otros usuarios.</p>}
            </div>
            {involvesAdmin && (
              <p className="mt-2 rounded-lg bg-yellow-50 px-3 py-2 text-xs font-medium text-yellow-700">
                Esta reunión involucra al administrador: quedará <b>pendiente de aprobación</b>.
              </p>
            )}
          </div>
          <Button onClick={submit} disabled={saving} className="w-full rounded-full bg-slate-900 py-6 text-base font-bold hover:bg-slate-800 active:scale-95" data-testid="submit-meeting-btn">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Enviar solicitud"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DetailDialog({ meeting, onClose }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);
  const [resDate, setResDate] = useState(meeting?.date || todayISO());
  const [resTime, setResTime] = useState(meeting?.time || "09:00");
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  if (!meeting) return null;

  const isAdmin = user?.role === "admin";
  const canDecide = isAdmin && meeting.status === "pendiente";
  const canCancel = isAdmin || meeting.requested_by === user?.id;

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); qc.invalidateQueries(); onClose(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!meeting} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="meeting-detail-dialog">
        <DialogHeader>
          <div className="mb-1 flex items-center gap-2"><StatusBadge status={meeting.status} /></div>
          <DialogTitle className="font-heading text-xl">{meeting.title}</DialogTitle>
          <DialogDescription>Solicitada por {meeting.requester_name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="flex flex-wrap gap-4 text-sm">
            <span className="inline-flex items-center gap-1.5 text-slate-600"><CalendarDays className="h-4 w-4 text-slate-400" /> {fmtDate(meeting.date)}</span>
            <span className="inline-flex items-center gap-1.5 text-slate-600"><Clock className="h-4 w-4 text-slate-400" /> {meeting.time}{meeting.end_time ? ` – ${meeting.end_time}` : ""}</span>
          </div>
          {meeting.topics && (
            <div>
              <div className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase text-slate-400"><MessageSquare className="h-3.5 w-3.5" /> Temas a tratar</div>
              <p className="whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-sm text-slate-700">{meeting.topics}</p>
            </div>
          )}
          <div>
            <div className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase text-slate-400"><Users2 className="h-3.5 w-3.5" /> Participantes</div>
            <div className="flex flex-wrap gap-2">
              {meeting.participants.map((p) => (
                <div key={p.id} className="flex items-center gap-2 rounded-full border border-slate-200 py-1 pl-1 pr-3">
                  <div className="flex h-6 w-6 items-center justify-center overflow-hidden rounded-full bg-slate-200 text-[10px] font-bold text-slate-600">
                    {p.avatar ? <img src={p.avatar} alt="" className="h-full w-full object-cover" /> : p.name[0]}
                  </div>
                  <span className="text-xs font-semibold text-slate-700">{p.name}{p.role === "admin" ? " (Admin)" : ""}</span>
                </div>
              ))}
            </div>
          </div>
          {meeting.status === "rechazada" && meeting.rejection_reason && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"><b>Rechazada:</b> {meeting.rejection_reason}</div>
          )}

          {canDecide && !rescheduling && !rejecting && (
            <div className="grid grid-cols-3 gap-2 border-t border-slate-100 pt-4">
              <Button onClick={() => act(() => api.post(`/meetings/${meeting.id}/approve`), "Reunión confirmada")} disabled={busy} className="rounded-full bg-emerald-500 font-bold hover:bg-emerald-600" data-testid="approve-meeting-btn"><ThumbsUp className="mr-1 h-4 w-4" /> Aprobar</Button>
              <Button onClick={() => setRescheduling(true)} disabled={busy} variant="outline" className="rounded-full font-bold" data-testid="reschedule-meeting-btn"><CalendarClock className="mr-1 h-4 w-4" /> Reprogramar</Button>
              <Button onClick={() => setRejecting(true)} disabled={busy} variant="outline" className="rounded-full border-red-300 font-bold text-red-600 hover:bg-red-50" data-testid="reject-meeting-btn"><XCircle className="mr-1 h-4 w-4" /> Rechazar</Button>
            </div>
          )}

          {canDecide && rescheduling && (
            <div className="space-y-3 rounded-lg border border-slate-200 p-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Nueva fecha</Label><Input type="date" value={resDate} onChange={(e) => setResDate(e.target.value)} className="mt-1" data-testid="reschedule-date" /></div>
                <div><Label>Nueva hora</Label><Input type="time" value={resTime} onChange={(e) => setResTime(e.target.value)} className="mt-1" data-testid="reschedule-time" /></div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1 rounded-full" onClick={() => setRescheduling(false)}>Cancelar</Button>
                <Button className="flex-1 rounded-full bg-slate-900 font-bold" disabled={busy} data-testid="confirm-reschedule-btn"
                  onClick={() => act(() => api.put(`/meetings/${meeting.id}/reschedule`, { date: resDate, time: resTime, end_time: meeting.end_time }), "Reunión reprogramada y confirmada")}>
                  Reprogramar y confirmar
                </Button>
              </div>
            </div>
          )}

          {canDecide && rejecting && (
            <div className="space-y-3 rounded-lg border border-slate-200 p-3">
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivo del rechazo" rows={2} data-testid="reject-reason-meeting" />
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1 rounded-full" onClick={() => setRejecting(false)}>Cancelar</Button>
                <Button className="flex-1 rounded-full bg-red-500 font-bold hover:bg-red-600" disabled={busy} data-testid="confirm-reject-meeting-btn"
                  onClick={() => reason.trim() ? act(() => api.post(`/meetings/${meeting.id}/reject`, { reason }), "Reunión rechazada") : toast.error("Indica el motivo")}>
                  Rechazar
                </Button>
              </div>
            </div>
          )}

          {canCancel && (
            <DialogFooter className="border-t border-slate-100 pt-4">
              <Button variant="ghost" className="text-red-500 hover:bg-red-50 hover:text-red-600" disabled={busy}
                onClick={() => window.confirm("¿Cancelar esta reunión?") && act(() => api.delete(`/meetings/${meeting.id}`), "Reunión cancelada")}
                data-testid="cancel-meeting-btn">
                Cancelar reunión
              </Button>
            </DialogFooter>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Meetings() {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [anchor, setAnchor] = useState(todayISO());

  const { data: stats } = useQuery({ queryKey: ["meetings-stats"], queryFn: () => api.get("/meetings/stats").then((r) => r.data) });
  const { data: meetings = [], isLoading } = useQuery({ queryKey: ["meetings"], queryFn: () => api.get("/meetings").then((r) => r.data) });

  const byDate = useMemo(() => {
    const m = {};
    meetings.forEach((mt) => { (m[mt.date] = m[mt.date] || []).push(mt); });
    return m;
  }, [meetings]);

  const monthCells = useMemo(() => {
    const d = new Date(anchor + "T00:00:00");
    const first = new Date(d.getFullYear(), d.getMonth(), 1);
    const offset = (first.getDay() + 6) % 7;
    const start = new Date(d.getFullYear(), d.getMonth(), 1 - offset);
    return Array.from({ length: 42 }, (_, i) => { const c = new Date(start); c.setDate(start.getDate() + i); return c.toISOString().slice(0, 10); });
  }, [anchor]);

  const shiftMonth = (delta) => { const d = new Date(anchor + "T00:00:00"); d.setDate(1); d.setMonth(d.getMonth() + delta); setAnchor(d.toISOString().slice(0, 10)); };
  const monthLabel = new Date(anchor + "T00:00:00").toLocaleDateString("es", { month: "long", year: "numeric" });

  const upcoming = [...meetings].filter((m) => m.date >= todayISO() && m.status !== "rechazada").sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));

  const CARDS = [
    { icon: CalendarCheck, label: "Agendadas", value: stats?.agendadas ?? 0, color: "text-blue-600", bg: "bg-blue-50", testid: "stat-agendadas" },
    { icon: Send, label: "Solicitadas", value: stats?.solicitadas ?? 0, color: "text-slate-700", bg: "bg-slate-100", testid: "stat-solicitadas" },
    { icon: Clock, label: "Pendientes", value: stats?.pendientes ?? 0, color: "text-yellow-600", bg: "bg-yellow-50", testid: "stat-pendientes" },
    { icon: CheckCircle2, label: "Aprobadas", value: stats?.aprobadas ?? 0, color: "text-emerald-600", bg: "bg-emerald-50", testid: "stat-aprobadas" },
    { icon: XCircle, label: "Rechazadas", value: stats?.rechazadas ?? 0, color: "text-red-600", bg: "bg-red-50", testid: "stat-rechazadas" },
  ];

  return (
    <div className="space-y-6" data-testid="meetings-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Reuniones</h1>
          <p className="text-sm text-slate-500">Agenda y solicita reuniones con tu equipo</p>
        </div>
        <Button onClick={() => setOpen(true)} className="rounded-full bg-slate-900 font-bold hover:bg-slate-800 active:scale-95" data-testid="request-meeting-btn">
          <Plus className="mr-1 h-4 w-4" /> Solicitar reunión
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        {CARDS.map((c) => <StatCard key={c.label} {...c} />)}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        {/* Calendar */}
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm lg:col-span-3" data-testid="meetings-calendar">
          <div className="mb-3 flex items-center justify-between">
            <button onClick={() => shiftMonth(-1)} className="rounded-lg border border-slate-200 p-2 text-slate-500 hover:bg-slate-50" data-testid="meet-month-prev"><ChevronLeft className="h-4 w-4" /></button>
            <span className="font-heading text-base font-bold capitalize text-slate-900">{monthLabel}</span>
            <button onClick={() => shiftMonth(1)} className="rounded-lg border border-slate-200 p-2 text-slate-500 hover:bg-slate-50" data-testid="meet-month-next"><ChevronRight className="h-4 w-4" /></button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-bold uppercase text-slate-400">
            {WEEK_LABELS.map((w) => <div key={w}>{w}</div>)}
          </div>
          <div className="mt-1 grid grid-cols-7 gap-1">
            {monthCells.map((cell) => {
              const items = byDate[cell] || [];
              const inMonth = cell.slice(0, 7) === anchor.slice(0, 7);
              const isToday = cell === todayISO();
              return (
                <div key={cell} className={cn("min-h-[58px] rounded-lg border p-1.5", inMonth ? "bg-white" : "bg-slate-50/50 opacity-50", isToday ? "border-slate-900" : "border-slate-200")}>
                  <div className={cn("text-xs font-bold", isToday ? "text-slate-900" : "text-slate-500")}>{Number(cell.slice(8, 10))}</div>
                  <div className="mt-1 space-y-0.5">
                    {items.slice(0, 2).map((mt) => (
                      <button key={mt.id} onClick={() => setSelected(mt)} data-testid={`cal-meeting-${mt.id}`}
                        className={cn("block w-full truncate rounded border-l-2 bg-slate-50 px-1 py-0.5 text-left text-[10px] font-medium text-slate-600 hover:bg-slate-100", M_STATUS[mt.status].border)}>
                        {mt.time} {mt.title}
                      </button>
                    ))}
                    {items.length > 2 && <div className="text-[9px] font-semibold text-slate-400">+{items.length - 2} más</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Upcoming list */}
        <div className="lg:col-span-2">
          <h2 className="mb-3 font-heading text-lg font-bold text-slate-900">Próximas reuniones</h2>
          {isLoading ? (
            <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
          ) : upcoming.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white py-12 text-center text-sm text-slate-400">
              No tienes reuniones próximas
            </div>
          ) : (
            <div className="space-y-3">
              {upcoming.map((mt) => (
                <button key={mt.id} onClick={() => setSelected(mt)} data-testid={`meeting-item-${mt.id}`}
                  className={cn("w-full rounded-xl border border-slate-200 border-l-4 bg-white p-3 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md", M_STATUS[mt.status].border)}>
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-heading text-sm font-bold text-slate-900">{mt.title}</h3>
                    <StatusBadge status={mt.status} />
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> {fmtDate(mt.date)}</span>
                    <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {mt.time}</span>
                    <span className="inline-flex items-center gap-1"><Users2 className="h-3.5 w-3.5" /> {mt.participants.length + 1}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <RequestDialog open={open} onOpenChange={setOpen} />
      <DetailDialog meeting={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
